/**
 * LobbyRenderer — multiplayer lobby (choose, quickplay, create, join, waiting).
 *
 * Extracted from MenuRenderer as a composition class.
 */

import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { Keys } from 'bonkjs';
import { WORLD, MENU } from '../../constants';
import type { GameState } from '../../types';

function monoStyle(size: number, color: number, bold = false): TextStyle {
    return new TextStyle({
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: size,
        fill: color,
        fontWeight: bold ? 'bold' : 'normal',
    });
}

export class LobbyRenderer {
    private lobbyTitle: Text;
    private lobbySubtitle: Text;
    private lobbyOptions: Text[] = [];
    private lobbyBox: Graphics;
    private lobbyHitRects: Graphics[] = [];
    private lobbyBackButton: Text;
    private lobbyBackHitRect: Graphics;
    private lobbyRoomCode: Text;
    private lobbyStatus: Text;
    private lobbyHint: Text;
    private lobbyCodeLabel: Text;
    private lobbyCursor: Graphics;

    constructor(
        container: Container,
        tapVirtualKey: (code: string) => void,
        getState: () => GameState | null,
    ) {
        const cx = WORLD.CANVAS_WIDTH / 2;

        // Full-screen event blocker
        const lobbyHitArea = new Graphics();
        lobbyHitArea.rect(0, 0, WORLD.CANVAS_WIDTH, WORLD.CANVAS_HEIGHT);
        lobbyHitArea.fill({ color: 0x000000, alpha: 0.001 });
        lobbyHitArea.eventMode = 'static';
        container.addChild(lobbyHitArea);

        // Title
        this.lobbyTitle = new Text({
            text: 'GEOMETRY BLAST',
            style: monoStyle(28, MENU.TITLE_LINE2_COLOR, true),
        });
        this.lobbyTitle.anchor.set(0.5);
        this.lobbyTitle.position.set(cx, 100);
        container.addChild(this.lobbyTitle);

        this.lobbySubtitle = new Text({
            text: 'ONLINE 1v1',
            style: monoStyle(MENU.MODE_SELECT_TITLE_SIZE, 0xffffff, true),
        });
        this.lobbySubtitle.anchor.set(0.5);
        this.lobbySubtitle.position.set(cx, 160);
        container.addChild(this.lobbySubtitle);

        // Options: Quickplay / Create / Join
        const lobbyOptionNames = ['QUICKPLAY', 'CREATE ROOM', 'JOIN ROOM'];
        const lobbyStartY = 300;
        const lobbySpacing = 80;
        const lobbyHitW = 400;
        const lobbyHitH = 60;
        for (let i = 0; i < lobbyOptionNames.length; i++) {
            const y = lobbyStartY + i * lobbySpacing;

            const hitRect = new Graphics();
            hitRect.position.set(cx - lobbyHitW / 2, y - lobbyHitH / 2);
            hitRect.eventMode = 'static';
            hitRect.cursor = 'pointer';
            const lobbyIdx = i;
            hitRect.on('pointerdown', () => {
                const s = getState();
                if (s?.multiplayer) s.multiplayer.lobbySelectIndex = lobbyIdx;
                tapVirtualKey(Keys.Space);
            });
            hitRect.on('pointerover', () => {
                const s = getState();
                if (s?.multiplayer) s.multiplayer.lobbySelectIndex = lobbyIdx;
            });
            container.addChild(hitRect);
            this.lobbyHitRects.push(hitRect);

            const label = new Text({
                text: lobbyOptionNames[i],
                style: monoStyle(MENU.MODE_LABEL_SIZE, 0xffffff, true),
            });
            label.anchor.set(0.5);
            label.position.set(cx, y);
            container.addChild(label);
            this.lobbyOptions.push(label);
        }

        // Selection box
        this.lobbyBox = new Graphics();
        container.addChild(this.lobbyBox);

        // Room code label
        this.lobbyCodeLabel = new Text({
            text: '',
            style: monoStyle(20, 0x888888),
        });
        this.lobbyCodeLabel.anchor.set(0.5);
        this.lobbyCodeLabel.position.set(cx, 300);
        container.addChild(this.lobbyCodeLabel);

        // Room code display
        this.lobbyRoomCode = new Text({
            text: '',
            style: monoStyle(64, 0x00ffff, true),
        });
        this.lobbyRoomCode.anchor.set(0.5);
        this.lobbyRoomCode.position.set(cx, 360);
        container.addChild(this.lobbyRoomCode);

        // Blinking cursor
        this.lobbyCursor = new Graphics();
        this.lobbyCursor.rect(0, -20, 3, 40);
        this.lobbyCursor.fill({ color: 0x00ffff });
        this.lobbyCursor.visible = false;
        container.addChild(this.lobbyCursor);

        // Status message
        this.lobbyStatus = new Text({
            text: '',
            style: monoStyle(18, 0xff8800),
        });
        this.lobbyStatus.anchor.set(0.5);
        this.lobbyStatus.position.set(cx, 440);
        container.addChild(this.lobbyStatus);

        // Controls hint (hidden)
        this.lobbyHint = new Text({
            text: '',
            style: monoStyle(MENU.HINT_FONT_SIZE, MENU.HINT_COLOR),
        });
        this.lobbyHint.visible = false;
        container.addChild(this.lobbyHint);

        // Back button
        this.lobbyBackHitRect = new Graphics();
        this.lobbyBackHitRect.eventMode = 'static';
        this.lobbyBackHitRect.cursor = 'pointer';
        this.lobbyBackHitRect.on('pointerdown', () => tapVirtualKey(Keys.Escape));
        container.addChild(this.lobbyBackHitRect);

        this.lobbyBackButton = new Text({
            text: 'BACK (ESC)',
            style: monoStyle(MENU.HINT_FONT_SIZE, MENU.HINT_COLOR),
        });
        this.lobbyBackButton.anchor.set(0.5);
        this.lobbyBackButton.position.set(cx, WORLD.CANVAS_HEIGHT - 100);
        container.addChild(this.lobbyBackButton);
    }

    update(state: GameState, elapsed: number): void {
        const mp = state.multiplayer;
        if (!mp) return;

        const cx = WORLD.CANVAS_WIDTH / 2;
        const lobbyHitW = 400;
        const lobbyHitH = 60;
        const r = MENU.SELECTION_CORNER_RADIUS;

        const spinner = this.getSpinner(elapsed);

        if (mp.lobbyAction === 'choose') {
            for (let i = 0; i < this.lobbyOptions.length; i++) {
                this.lobbyOptions[i].visible = true;
                this.lobbyHitRects[i].visible = true;
                const isSelected = i === mp.lobbySelectIndex;
                this.lobbyOptions[i].style.fill = isSelected ? 0x00ffff : 0x888888;

                this.lobbyHitRects[i].clear();
                this.lobbyHitRects[i].rect(0, 0, lobbyHitW, lobbyHitH);
                this.lobbyHitRects[i].fill({ color: 0x000000, alpha: 0.001 });
            }
            this.lobbyBox.clear();
            const startY = 300;
            const spacing = 80;
            const y = startY + mp.lobbySelectIndex * spacing;
            const boxW = 350;
            const boxH = 50;
            this.lobbyBox.roundRect(cx - boxW / 2, y - boxH / 2, boxW, boxH, r);
            this.lobbyBox.fill({ color: 0x00ffff, alpha: MENU.SELECTION_FILL_ALPHA });
            this.lobbyBox.roundRect(cx - boxW / 2, y - boxH / 2, boxW, boxH, r);
            this.lobbyBox.stroke({ color: 0x00ffff, width: 2 });
            this.lobbyBox.visible = true;

            this.lobbyCodeLabel.visible = false;
            this.lobbyRoomCode.visible = false;
            this.lobbyCursor.visible = false;
            this.lobbyStatus.visible = false;
        } else if (mp.lobbyAction === 'quickplay') {
            for (const opt of this.lobbyOptions) opt.visible = false;
            for (const hr of this.lobbyHitRects) hr.visible = false;
            this.lobbyBox.visible = false;
            this.lobbyCursor.visible = false;

            this.lobbyCodeLabel.visible = true;
            this.lobbyCodeLabel.text = 'PLAYING AS:';

            this.lobbyRoomCode.visible = true;
            this.lobbyRoomCode.text = mp.quickplayName || '...';

            this.lobbyStatus.visible = true;
            const statusText = mp.lobbyStatus || 'Searching for opponent...';
            this.lobbyStatus.text = `${statusText} ${spinner}`;
            this.lobbyStatus.alpha = 0.5 + 0.5 * Math.sin(elapsed * 3);

        } else if (mp.lobbyAction === 'create') {
            for (const opt of this.lobbyOptions) opt.visible = false;
            for (const hr of this.lobbyHitRects) hr.visible = false;
            this.lobbyBox.visible = false;
            this.lobbyCursor.visible = false;

            this.lobbyCodeLabel.visible = true;
            this.lobbyCodeLabel.text = 'ROOM CODE:';

            this.lobbyRoomCode.visible = true;
            this.lobbyRoomCode.text = mp.roomCode || '...';

            this.lobbyStatus.visible = true;
            const statusText = mp.lobbyStatus || 'Waiting for opponent...';
            this.lobbyStatus.text = `${statusText} ${spinner}`;
            this.lobbyStatus.alpha = 0.6 + 0.4 * Math.sin(elapsed * 3);

        } else if (mp.lobbyAction === 'join') {
            for (const opt of this.lobbyOptions) opt.visible = false;
            for (const hr of this.lobbyHitRects) hr.visible = false;
            this.lobbyBox.visible = false;

            this.lobbyCodeLabel.visible = true;
            this.lobbyCodeLabel.text = 'ENTER ROOM CODE:';

            this.lobbyRoomCode.visible = true;
            if (mp.roomCodeInput) {
                this.lobbyRoomCode.text = mp.roomCodeInput;
                this.lobbyRoomCode.style.fill = 0x00ffff;
            } else {
                this.lobbyRoomCode.text = 'CODE';
                this.lobbyRoomCode.style.fill = 0x444444;
            }

            this.lobbyCursor.visible = true;
            const textWidth = mp.roomCodeInput ? this.lobbyRoomCode.width : 0;
            this.lobbyCursor.position.set(cx + textWidth / 2 + 4, 360);
            this.lobbyCursor.alpha = Math.sin(elapsed * 6) > 0 ? 1 : 0;

            if (mp.lobbyStatus) {
                this.lobbyStatus.visible = true;
                let statusText = mp.lobbyStatus;
                if (statusText.includes('Joining') || statusText.includes('...')) {
                    statusText += ` ${spinner}`;
                }
                this.lobbyStatus.text = statusText;
                this.lobbyStatus.alpha = 0.6 + 0.4 * Math.sin(elapsed * 3);
            } else {
                this.lobbyStatus.visible = false;
            }

        }

        // mp_waiting phase
        if (state.phase === 'mp_waiting') {
            for (const opt of this.lobbyOptions) opt.visible = false;
            for (const hr of this.lobbyHitRects) hr.visible = false;
            this.lobbyBox.visible = false;
            this.lobbyCursor.visible = false;
            this.lobbyCodeLabel.visible = false;

            this.lobbyRoomCode.visible = true;
            this.lobbyRoomCode.text = `VS ${mp.opponentName || 'OPPONENT'}`;

            this.lobbyStatus.visible = true;
            this.lobbyStatus.text = `Loading... ${spinner}`;
            this.lobbyStatus.alpha = 0.6 + 0.4 * Math.sin(elapsed * 3);

        }

        this.lobbyHint.visible = this.lobbyHint.text !== '';

        // Back button
        this.lobbyBackButton.visible = true;
        this.lobbyBackHitRect.visible = true;
    }

    destroy(): void {
        // Children are destroyed with container by parent
    }

    private getSpinner(elapsed: number, speed: number = 8): string {
        const frame = Math.floor(elapsed * speed) % 4;
        return ['|', '/', '-', '\\'][frame];
    }
}
